import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import Dropdown from "../Components/Dropdown"; // Replace with your Dropdown component
import ApplicationLogo from "../Components/ApplicationLogo"; // Replace with your Logo component
import { useAuth } from "../context/AuthContext";
import { useTopCategories } from "../hooks/useTopCategories";

export default function AuthenticatedLayout({ header, children }) {
  const [showingNavigationDropdown, setShowingNavigationDropdown] = useState(false);
  const [navCategories, setNavCategories] = useState([]);
  const [isMenuSubOpen, setIsMenuSubOpen] = useState(false);
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { topCategories: topLevelCategories, isLoading: isLoadingTopCategories } = useTopCategories({
    enabled: Boolean(user),
  });
  const isCategoriesView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("view") === "categories";
  }, [location.search]);
  const canViewConfigurationMenu = Boolean(user);

  const slugRouteMap = useMemo(
    () => ({
      coffees: '/coffees',
      drinks: '/drinks',
    }),
    []
  );

  const mapCategoryToRoute = useCallback(
    (category) => {
      const slug = category.slug ? category.slug.toLowerCase() : '';
      if (slug) {
        return slugRouteMap[slug] || `/${slug}`;
      }
      return `/coffees?view=categories&root=${category.id}`;
    },
    [slugRouteMap]
  );

  const mergeStaticCategories = useCallback(
    (apiCategories) => {
      if (!Array.isArray(apiCategories)) {
        setNavCategories([]);
        return;
      }

      const topLevel = apiCategories
        .filter(
          (category) =>
            category && (category.parent_id === null || category.parent_id === undefined)
        )
        .map((category) => ({
          id: String(category.id),
          slug: category.slug,
          label: category.name || category.slug || `Κατηγορία ${category.id}`,
          route: mapCategoryToRoute(category),
        }));

      setNavCategories(topLevel);
    },
    [mapCategoryToRoute]
  );

  useEffect(() => {
    if (!user) {
      setNavCategories([]);
      return;
    }

    if (!isLoadingTopCategories) {
      mergeStaticCategories(topLevelCategories);
    }
  }, [user, mergeStaticCategories, topLevelCategories, isLoadingTopCategories]);

  const handleLogout = () => {
    console.log("Logging out...");
    logout();
    navigate("/login");
  };

  const getNavLinkClasses = useCallback(
    (active) =>
      active
        ? "text-gray-800 font-medium m-auto"
        : "text-gray-500 hover:text-gray-700 m-auto",
    []
  );

  const getMobileNavLinkClasses = useCallback(
    (active) =>
      active
        ? "block px-4 py-2 text-gray-800 font-medium bg-gray-50"
        : "block px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
    []
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left Section */}
            <div className="flex items-center space-x-6">
              <div className="shrink-0 flex items-center">
                <Link to="/">
                  <ApplicationLogo className="block h-9 w-auto fill-current text-gray-800" />
                </Link>
              </div>
              <div className="hidden sm:flex space-x-6">
                {user && (
                  <>
                    <Dropdown>
                      <Dropdown.Trigger>
                        <button
                          className="group inline-flex items-center space-x-1 text-gray-500 hover:text-gray-700"
                          onClick={() => setIsProductsMenuOpen((prev) => !prev)}
                        >
                          <span>Προϊόντα</span>
                          <span className={`transition-transform ${isProductsMenuOpen ? 'rotate-180' : ''}`}>
                            ▾
                          </span>
                        </button>
                      </Dropdown.Trigger>
                      <Dropdown.Content
                        side="bottom"
                        align="start"
                        onMouseEnter={() => setIsProductsMenuOpen(true)}
                        onMouseLeave={() => setIsProductsMenuOpen(false)}
                      >
                        {navCategories.length ? (
                          navCategories.map((category) => (
                            <NavLink
                              key={category.id}
                              to={category.route}
                              className={({ isActive }) =>
                                `block px-4 py-2 text-sm ${
                                  isActive && !isCategoriesView
                                    ? 'text-gray-900 font-medium'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`
                              }
                              onClick={() => setIsProductsMenuOpen(false)}
                            >
                              {category.label}
                            </NavLink>
                          ))
                        ) : (
                          <span className="block px-4 py-2 text-sm text-gray-500">
                            Δεν υπάρχουν κατηγορίες
                          </span>
                        )}
                      </Dropdown.Content>
                    </Dropdown>
                    {canViewConfigurationMenu && (
                      <Dropdown>
                        <Dropdown.Trigger>
                          <button className="group inline-flex items-center space-x-1 text-gray-500 hover:text-gray-700">
                            <span>Διαμόρφωση</span>
                            <span className="transition-transform group-hover:rotate-180">▾</span>
                          </button>
                        </Dropdown.Trigger>
                        <Dropdown.Content side="bottom" align="start">
                          <NavLink
                            to="/coffees?view=categories"
                            className={({ isActive }) =>
                              `block px-4 py-2 text-sm ${
                                isActive || isCategoriesView ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-800'
                              }`
                            }
                          >
                            Κατηγορίες
                          </NavLink>
                          <NavLink
                            to="/private-blend"
                            className={({ isActive }) =>
                              `block px-4 py-2 text-sm ${
                                isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-800'
                              }`
                            }
                          >
                            Private Blend
                          </NavLink>

                          <div
                            className="relative border-t pt-3"
                            onMouseEnter={() => setIsMenuSubOpen(true)}
                            onMouseLeave={() => setIsMenuSubOpen(false)}
                          >
                            <button
                              className="flex w-full items-center justify-between rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                              onClick={() => setIsMenuSubOpen((prev) => !prev)}
                            >
                              <span>Μενού</span>
                              <span className={`transition-transform ${isMenuSubOpen ? 'rotate-90' : ''}`}>
                                ▸
                              </span>
                            </button>
                            {isMenuSubOpen && (
                              <div className="absolute left-full top-1/2 z-50 ml-2 w-44 -translate-y-1/2 rounded-md border border-gray-200 bg-white shadow-lg">
                                <NavLink
                                  to="/menu-builder"
                                  className={({ isActive }) =>
                                    `block px-3 py-2 text-sm ${
                                      isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-800'
                                    }`
                                  }
                                >
                                  Header
                                </NavLink>
                                <NavLink
                                  to="/menu-builder-footer"
                                  className={({ isActive }) =>
                                    `block px-3 py-2 text-sm ${
                                      isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-800'
                                    }`
                                  }
                                >
                                  Footer
                                </NavLink>
                              </div>
                            )}
                          </div>

                          <div className="border-t pt-3">
                            <NavLink
                              to="/settings"
                              className={({ isActive }) =>
                                `block px-4 py-2 text-sm ${
                                  isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-800'
                                }`
                              }
                            >
                              Γενικές Ρυθμίσεις
                            </NavLink>
                            <NavLink
                              to="/new-user"
                              className={({ isActive }) =>
                                `block px-4 py-2 text-sm ${
                                  isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-800'
                                }`
                              }
                            >
                              Νέος Χρήστης
                            </NavLink>
                          </div>
                        </Dropdown.Content>
                      </Dropdown>
                    )}
                    <NavLink
                      to="/media"
                      className={({ isActive }) =>
                        isActive
                          ? "text-gray-800 font-medium m-auto"
                          : "text-gray-500 hover:text-gray-700 m-auto"
                      }
                    >
                      Βιβλιοθήκη Πολυμέσων
                    </NavLink>
                  </>
                )}
              </div>
            </div>

            {/* Right Section */}
            <div className="hidden sm:flex items-center space-x-6">
              {user ? (
                <div className="relative">
                  <Dropdown>
                    <Dropdown.Trigger>
                      <button className="inline-flex items-center px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
                        {user.name}
                      </button>
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                      <NavLink to="/profile" className="block px-4 py-2 hover:bg-gray-100">
                        Προφίλ
                      </NavLink>
                      <NavLink to="/new-user" className="block px-4 py-2 hover:bg-gray-100">
                        Νέος Χρήστης
                      </NavLink>
                      <button
                        onClick={handleLogout}
                        className="block px-4 py-2 text-left w-full hover:bg-gray-100"
                      >
                        Αποσύνδεση
                      </button>
                    </Dropdown.Content>
                  </Dropdown>
                </div>
              ) : (
                <NavLink
                  to="/login"
                  className="text-gray-500 hover:text-gray-700"
                >
                  Σύνδεση
                </NavLink>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="sm:hidden flex items-center">
              <button
                onClick={() =>
                  setShowingNavigationDropdown(!showingNavigationDropdown)
                }
                className="p-2 rounded-md text-gray-400 hover:bg-gray-100 focus:outline-none"
              >
                ☰
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {showingNavigationDropdown && (
          <div className="sm:hidden bg-white border-t border-gray-100">
            <div className="space-y-1 py-4">
              {user && (
                <>
                  <div className="border-t border-gray-100 pt-2">
                    <p className="px-4 text-xs font-semibold uppercase text-gray-400">Προϊόντα</p>
                    {navCategories.map((category) => (
                      <NavLink
                        key={category.id}
                        to={category.route}
                        className={({ isActive }) =>
                          getMobileNavLinkClasses(isActive && !isCategoriesView)
                        }
                      >
                        {category.label}
                      </NavLink>
                    ))}
                  </div>
                  {canViewConfigurationMenu && (
                    <div className="border-t border-gray-100 pt-2">
                      <p className="px-4 text-xs font-semibold uppercase text-gray-400">Διαμόρφωση</p>
                      <NavLink
                        to="/coffees?view=categories"
                        className={() => getMobileNavLinkClasses(isCategoriesView)}
                      >
                        Κατηγορίες
                      </NavLink>
                      <p className="px-4 mt-2 text-xs font-semibold uppercase text-gray-400">Μενού</p>
                      <NavLink
                        to="/menu-builder"
                        className={({ isActive }) => getMobileNavLinkClasses(isActive)}
                      >
                        Header
                      </NavLink>
                      <NavLink
                        to="/menu-builder-footer"
                        className={({ isActive }) => getMobileNavLinkClasses(isActive)}
                      >
                        Footer
                      </NavLink>
                      <p className="px-4 mt-2 text-xs font-semibold uppercase text-gray-400">Άλλα</p>
                      <NavLink
                        to="/settings"
                        className={({ isActive }) => getMobileNavLinkClasses(isActive)}
                      >
                        Γενικές Ρυθμίσεις
                      </NavLink>
                      <NavLink
                        to="/new-user"
                        className={({ isActive }) => getMobileNavLinkClasses(isActive)}
                      >
                        Νέος Χρήστης
                      </NavLink>
                    </div>
                  )}
                  <NavLink
                    to="/media"
                    className="block px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    Βιβλιοθήκη Πολυμέσων
                  </NavLink>
                  <NavLink
                    to="/profile"
                    className="block px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    Προφίλ
                  </NavLink>
                  <NavLink
                    to="/new-user"
                    className="block px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    Νέος Χρήστης
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className="block w-full px-4 py-2 text-left text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    Αποσύνδεση
                  </button>
                </>
              )}
              {!user && (
                <NavLink
                  to="/login"
                  className="block px-4 py-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  Σύνδεση
                </NavLink>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      {header && (
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {header}
          </div>
        </header>
      )}

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
}
